import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PawPrint, ChevronDownIcon, CheckIcon } from 'lucide-react';
import { ProgressLoader } from './components/ProgressLoader';
import { RoutineLoader } from './components/RoutineLoader';
const ageOptions = [{
  id: 'cachorro',
  title: 'Cachorro',
  desc: 'Menor a 6 meses',
  emoji: '🐶'
}, {
  id: 'adolescente',
  title: 'Perro adolescente',
  desc: '6 a 18 meses',
  emoji: '🐕'
}, {
  id: 'adulto',
  title: 'Perro adulto',
  desc: '1.5 a 7 años',
  emoji: '🦮'
}, {
  id: 'senior',
  title: 'Perro senior',
  desc: 'Mayor a 7 años',
  emoji: '🐩'
}];
const genderOptions = [{
  id: 'macho',
  title: 'Macho',
  symbol: '♂',
  accentColor: '#3b82f6',
  accentBg: '#eff6ff',
  accentText: '#1e3a5f',
  accentSubtext: '#3b82f6'
}, {
  id: 'hembra',
  title: 'Hembra',
  symbol: '♀',
  accentColor: '#ec4899',
  accentBg: '#fdf2f8',
  accentText: '#831843',
  accentSubtext: '#ec4899'
}];
const breedPills = ['Mestizo', 'Chihuahua', 'Yorkshire Terrier', 'Shih Tzu', 'Maltés', 'Pomerania', 'Maltipoo', 'Cavalier King Charles', 'Habanero', 'Schnauzer Miniatura'];
const otherBreeds = ['Bichón Frisé', 'Boston Terrier', 'Bulldog Francés', 'Caniche Toy', 'Cocker Spaniel', 'Corgi', 'Dachshund', 'Jack Russell Terrier', 'Lhasa Apso', 'Papillón', 'Pekinés', 'Pinscher Miniatura', 'Pug', 'Scottish Terrier', 'West Highland Terrier'];
const situationOptions = [{
  id: 'control',
  title: 'A veces siento que pierdo el control con mi perro'
}, {
  id: 'lugares',
  title: 'Es difícil llevarlo a cualquier lugar sin preocuparme'
}, {
  id: 'rutina',
  title: 'Quiero una rutina simple que realmente funcione'
}];
const pageVariants = {
  enter: {
    opacity: 0,
    x: 60
  },
  center: {
    opacity: 1,
    x: 0
  },
  exit: {
    opacity: 0,
    x: -60
  }
};
const sectionVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
export function App() {
  const [step, setStep] = useState(1);
  const [selectedAge, setSelectedAge] = useState<string | null>(null);
  const [selectedGender, setSelectedGender] = useState<string | null>(null);
  const [selectedBreed, setSelectedBreed] = useState<string | null>(null);
  const [dropdownBreed, setDropdownBreed] = useState('');
  // New state for progressive reveal step
  const [section1Selections, setSection1Selections] = useState<string[]>([]);
  const [section2Selection, setSection2Selection] = useState<string | null>(null);
  const [section3Selection, setSection3Selection] = useState<string | null>(null);
  const [section4Selections, setSection4Selections] = useState<string[]>([]);
  const [petName, setPetName] = useState('');
  const [pottySelections, setPottySelections] = useState<string[]>([]);
  const [selectedSituations, setSelectedSituations] = useState<string[]>([]);
  const [step11Loading, setStep11Loading] = useState(true);
  const section2Ref = useRef<HTMLDivElement>(null);
  const section3Ref = useRef<HTMLDivElement>(null);
  const section4Ref = useRef<HTMLDivElement>(null);
  // Auto-scroll when sections appear
  useEffect(() => {
    if (section1Selections.length > 0 && section2Ref.current) {
      setTimeout(() => {
        section2Ref.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 200);
    }
  }, [section1Selections.length > 0]);
  useEffect(() => {
    if (section2Selection && section3Ref.current) {
      setTimeout(() => {
        section3Ref.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 200);
    }
  }, [section2Selection]);
  useEffect(() => {
    if (section3Selection && section4Ref.current) {
      setTimeout(() => {
        section4Ref.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 200);
    }
  }, [section3Selection]);
  // Initialize Meta Pixel
  useEffect(() => {
    if (!(window as any).fbq) {
      const f = window as any;
      const n = f.fbq = function (...args: any[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [] as any[];
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }
    ;
    (window as any).fbq('init', '945755351435014');
    (window as any).fbq('track', 'PageView');
    // Initialize Microsoft Clarity
    if (!(window as any).clarity) {
      ;
      (function (c: any, l: any, a: string, r: string, i: string) {
        c[a] = c[a] || function (...args: any[]) {
          ;
          (c[a].q = c[a].q || []).push(args);
        };
        const t = l.createElement(r) as HTMLScriptElement;
        t.async = true;
        t.src = 'https://www.clarity.ms/tag/' + i;
        const y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);
      })(window, document, 'clarity', 'script', 'widynndqam');
    }
  }, []);
  function handleAgeSelect(id: string) {
    setSelectedAge(id);
    setTimeout(() => setStep(2), 400);
  }
  function handleGenderSelect(id: string) {
    setSelectedGender(id);
    setTimeout(() => setStep(3), 400);
  }
  function handleBreedPillSelect(breed: string) {
    setSelectedBreed(breed);
    setDropdownBreed('');
  }
  function handleDropdownChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const value = e.target.value;
    setDropdownBreed(value);
    if (value) {
      setSelectedBreed(value);
    } else {
      setSelectedBreed(null);
    }
  }
  function handleContinueBreed() {
    setStep(4);
  }
  function handleSection1Toggle(id: string) {
    if (id === 'todas') {
      setSection1Selections(prev => prev.includes('todas') ? [] : ['todas']);
    } else {
      setSection1Selections(prev => {
        const without = prev.filter(s => s !== 'todas');
        return without.includes(id) ? without.filter(s => s !== id) : [...without, id];
      });
    }
  }
  function handleSection2Select(id: string) {
    setSection2Selection(id);
  }
  function handleSection3Select(id: string) {
    setSection3Selection(id);
  }
  function handleSection4Toggle(id: string) {
    if (id === 'todas') {
      setSection4Selections(prev => prev.includes('todas') ? [] : ['todas']);
    } else {
      setSection4Selections(prev => {
        const without = prev.filter(s => s !== 'todas');
        return without.includes(id) ? without.filter(s => s !== id) : [...without, id];
      });
    }
  }
  function handlePottyToggle(id: string) {
    if (id === 'todas') {
      setPottySelections(prev => prev.includes('todas') ? [] : ['todas']);
    } else {
      setPottySelections(prev => {
        const without = prev.filter(s => s !== 'todas');
        return without.includes(id) ? without.filter(s => s !== id) : [...without, id];
      });
    }
  }
  function handleSituationToggle(id: string) {
    setSelectedSituations(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  }
  const selectedGoalTitle = section1Selections.includes('todas') ? 'todas las áreas' : section1Selections.map(id => {
    const options = [{
      id: 'ladra',
      title: 'ladra demasiado'
    }, {
      id: 'energia',
      title: 'demasiada energía'
    }, {
      id: 'agresivo',
      title: 'reactividad o agresividad'
    }, {
      id: 'correa',
      title: 'tira de la correa'
    }, {
      id: 'ansiedad',
      title: 'ansiedad por separación'
    }, {
      id: 'accidentes',
      title: 'accidentes en casa'
    }, {
      id: 'destructivo',
      title: 'rompe cosas'
    }];
    return options.find(o => o.id === id)?.title;
  }).filter(Boolean).join(', ') || '';
  const totalSteps = 12;
  // Map section1 selections to readable detected behaviors
  const detectedBehaviors = section1Selections.includes('todas') ? [{
    emoji: '🗣️',
    label: 'Ladra en exceso'
  }, {
    emoji: '⚡',
    label: 'Demasiada energía acumulada'
  }, {
    emoji: '😤',
    label: 'Reacciona fácilmente'
  }, {
    emoji: '🐕‍🦺',
    label: 'Tira de la correa'
  }, {
    emoji: '😢',
    label: 'Ansiedad por separación'
  }, {
    emoji: '💧',
    label: 'Accidentes en casa'
  }, {
    emoji: '🪑',
    label: 'Comportamiento destructivo'
  }] : [section1Selections.includes('ladra') && {
    emoji: '🗣️',
    label: 'Ladra en exceso'
  }, section1Selections.includes('energia') && {
    emoji: '⚡',
    label: 'Demasiada energía acumulada'
  }, section1Selections.includes('agresivo') && {
    emoji: '😤',
    label: 'Reacciona fácilmente'
  }, section1Selections.includes('correa') && {
    emoji: '🐕‍🦺',
    label: 'Tira de la correa'
  }, section1Selections.includes('ansiedad') && {
    emoji: '😢',
    label: 'Ansiedad por separación'
  }, section1Selections.includes('accidentes') && {
    emoji: '💧',
    label: 'Accidentes en casa'
  }, section1Selections.includes('destructivo') && {
    emoji: '🪑',
    label: 'Comportamiento destructivo'
  }].filter(Boolean) as {
    emoji: string;
    label: string;
  }[];
  const mannersScore = detectedBehaviors.length >= 5 ? 2.4 : detectedBehaviors.length >= 3 ? 3.1 : 4.2;
  const scorePercent = mannersScore / 10 * 100;
  return <div className="min-h-screen flex justify-center text-stone-800 selection:bg-orange-200" data-id="element-0">
      {/* Mobile App Container */}
      <div className="w-full max-w-[420px] bg-[#F7F7F5] min-h-screen flex flex-col relative shadow-2xl shadow-stone-200/50 sm:border-x border-stone-200/50 overflow-hidden" data-id="element-1">
        {/* Header */}
        <header className="pt-10 pb-6 px-6 flex items-center justify-center gap-2" data-id="element-2">
          <div className="bg-orange-500 p-1.5 rounded-xl shadow-sm" data-id="element-3">
            <PawPrint className="w-5 h-5 text-white" strokeWidth={2.5} data-id="element-4" />
          </div>
          <span className="text-xl font-extrabold tracking-tight text-stone-900" data-id="element-5">
            KALMIA
          </span>
        </header>

        {/* Step indicator */}
        <div className="px-6 flex items-center gap-2 justify-center mb-2" data-id="element-6">
          {Array.from({
          length: totalSteps
        }, (_, i) => i + 1).map(s => <div key={s} className={`h-1 rounded-full transition-all duration-300 ${s <= step ? 'bg-orange-500 w-10' : 'bg-stone-200 w-6'}`} data-id="element-7" />)}
        </div>

        {/* Main Content with page transitions */}
        <main className="flex-1 px-6 flex flex-col overflow-y-auto" data-id="element-8">
          <AnimatePresence mode="wait" data-id="element-9">
            {/* STEP 1 — Age */}
            {step === 1 && <motion.div key="step-age" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex flex-col flex-1" data-id="element-10">
                <div className="text-center mt-2 mb-10" data-id="element-11">
                  <h1 className="text-[26px] font-extrabold tracking-tight text-stone-900 mb-4 leading-[1.15]" data-id="element-12">
                    OBTÉN UNA RUTINA PERSONALIZADA PARA PERROS PEQUEÑOS
                  </h1>
                  <p className="text-xs font-bold text-stone-400 tracking-[0.15em] uppercase leading-relaxed px-4" data-id="element-13">
                    Basado en la edad y comportamiento de tu perro
                  </p>
                </div>

                <div className="flex flex-col gap-4" data-id="element-14">
                  {ageOptions.map(option => {
                const isSelected = selectedAge === option.id;
                return <motion.button key={option.id} onClick={() => handleAgeSelect(option.id)} whileHover={{
                  scale: 1.015
                }} whileTap={{
                  scale: 0.98
                }} initial={false} animate={{
                  borderColor: isSelected ? '#f97316' : 'transparent',
                  backgroundColor: isSelected ? '#fff7ed' : '#ffffff'
                }} transition={{
                  duration: 0.2,
                  ease: 'easeOut'
                }} className="relative w-full text-left p-5 rounded-[20px] flex items-center justify-between border-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.06)]" data-id="element-15">
                        <div className="pr-4" data-id="element-16">
                          <h3 className={`font-bold text-[17px] mb-1 transition-colors duration-200 ${isSelected ? 'text-orange-900' : 'text-stone-800'}`} data-id="element-17">
                            {option.title}
                          </h3>
                          <p className={`text-[14px] font-medium transition-colors duration-200 ${isSelected ? 'text-orange-700/80' : 'text-stone-500'}`} data-id="element-18">
                            {option.desc}
                          </p>
                        </div>
                        <div className="text-3xl drop-shadow-sm" data-id="element-19">
                          {option.emoji}
                        </div>
                        {isSelected && <motion.div layoutId="activeDot" className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-orange-500 rounded-full shadow-sm" data-id="element-20" />}
                      </motion.button>;
              })}
                </div>
              </motion.div>}

            {/* STEP 2 — Gender */}
            {step === 2 && <motion.div key="step-gender" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex flex-col flex-1" data-id="element-21">
                <div className="text-center mt-2 mb-10" data-id="element-22">
                  <h1 className="text-[26px] font-extrabold tracking-tight text-stone-900 mb-4 leading-[1.15]" data-id="element-23">
                    ¿TU PERRO ES?
                  </h1>
                  <p className="text-xs font-bold text-stone-400 tracking-[0.15em] uppercase leading-relaxed px-4" data-id="element-24">
                    Selecciona el sexo de tu perro
                  </p>
                </div>

                <div className="flex flex-col gap-5" data-id="element-25">
                  {genderOptions.map(option => {
                const isSelected = selectedGender === option.id;
                return <motion.button key={option.id} onClick={() => handleGenderSelect(option.id)} whileHover={{
                  scale: 1.015
                }} whileTap={{
                  scale: 0.98
                }} initial={false} animate={{
                  borderColor: isSelected ? option.accentColor : 'transparent',
                  backgroundColor: isSelected ? option.accentBg : '#ffffff'
                }} transition={{
                  duration: 0.2,
                  ease: 'easeOut'
                }} className="relative w-full text-left p-6 rounded-[20px] flex items-center gap-5 border-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.06)]" data-id="element-26">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl font-bold shrink-0 transition-colors duration-200" style={{
                    backgroundColor: isSelected ? option.accentBg : '#f5f5f0',
                    color: option.accentColor
                  }} data-id="element-27">
                          {option.symbol}
                        </div>
                        <h3 className="font-bold text-[19px] transition-colors duration-200" style={{
                    color: isSelected ? option.accentText : '#292524'
                  }} data-id="element-28">
                          {option.title}
                        </h3>
                        {isSelected && <motion.div layoutId="genderDot" className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full shadow-sm" style={{
                    backgroundColor: option.accentColor
                  }} data-id="element-29" />}
                      </motion.button>;
              })}
                </div>

                <button onClick={() => {
              setStep(1);
              setSelectedGender(null);
            }} className="mt-8 text-sm font-semibold text-stone-400 hover:text-stone-600 transition-colors mx-auto" data-id="element-30">
                  ← Volver
                </button>
              </motion.div>}

            {/* STEP 3 — Breed */}
            {step === 3 && <motion.div key="step-breed" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex flex-col flex-1 pb-4" data-id="element-31">
                <div className="text-center mt-2 mb-10" data-id="element-32">
                  <h1 className="text-[26px] font-extrabold tracking-tight text-stone-900 mb-4 leading-[1.15]" data-id="element-33">
                    ¿CUÁL ES LA RAZA DE TU PERRO?
                  </h1>
                  <p className="text-xs font-bold text-stone-400 tracking-[0.15em] uppercase leading-relaxed px-4" data-id="element-34">
                    Rutina de modales para perros pequeños según la raza de tu
                    perro
                  </p>
                </div>

                {/* Breed pills */}
                <div className="flex flex-wrap gap-3 justify-center mb-8" data-id="element-35">
                  {breedPills.map(breed => {
                const isSelected = selectedBreed === breed && !dropdownBreed;
                return <motion.button key={breed} onClick={() => handleBreedPillSelect(breed)} whileHover={{
                  scale: 1.04
                }} whileTap={{
                  scale: 0.96
                }} className={`px-4 py-2.5 rounded-full text-[14px] font-semibold border-2 transition-all duration-200 shadow-[0_2px_10px_rgba(0,0,0,0.03)] ${isSelected ? 'bg-orange-50 border-orange-500 text-orange-900' : 'bg-white border-transparent text-stone-700 hover:border-stone-200'}`} data-id="element-36">
                        {breed}
                      </motion.button>;
              })}
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-6 px-2" data-id="element-37">
                  <div className="flex-1 h-px bg-stone-200" data-id="element-38" />
                  <span className="text-xs font-bold text-stone-400 tracking-wider uppercase" data-id="element-39">
                    o
                  </span>
                  <div className="flex-1 h-px bg-stone-200" data-id="element-40" />
                </div>

                {/* Dropdown for other breeds */}
                <div className="relative mb-8" data-id="element-41">
                  <label className="block text-[13px] font-bold text-stone-500 mb-2 ml-1" data-id="element-42">
                    Otra raza
                  </label>
                  <div className="relative" data-id="element-43">
                    <select value={dropdownBreed} onChange={handleDropdownChange} className="w-full appearance-none bg-white border-2 border-transparent rounded-[16px] px-5 py-4 text-[15px] font-medium text-stone-800 shadow-[0_4px_20px_rgba(0,0,0,0.03)] focus:outline-none focus:border-orange-500 focus:ring-0 transition-colors duration-200 cursor-pointer" data-id="element-44">
                      <option value="" data-id="element-45">Seleccionar raza</option>
                      {otherBreeds.map(breed => <option key={breed} value={breed} data-id="element-46">
                          {breed}
                        </option>)}
                    </select>
                    <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400 pointer-events-none" data-id="element-47" />
                  </div>
                </div>

                {/* Continue button */}
                <motion.button onClick={handleContinueBreed} disabled={!selectedBreed} whileHover={selectedBreed ? {
              scale: 1.02
            } : {}} whileTap={selectedBreed ? {
              scale: 0.98
            } : {}} className={`w-full py-4 rounded-[16px] text-[16px] font-bold tracking-wide transition-all duration-300 shadow-[0_4px_20px_rgba(0,0,0,0.06)] ${selectedBreed ? 'bg-orange-500 text-white hover:bg-orange-600 cursor-pointer' : 'bg-stone-200 text-stone-400 cursor-not-allowed'}`} data-id="element-48">
                  Continuar
                </motion.button>

                {/* Back button */}
                <button onClick={() => {
              setStep(2);
              setSelectedBreed(null);
              setDropdownBreed('');
            }} className="mt-6 text-sm font-semibold text-stone-400 hover:text-stone-600 transition-colors mx-auto" data-id="element-49">
                  ← Volver
                </button>
              </motion.div>}

            {/* STEP 4 — Social Proof Transition */}
            {step === 4 && <motion.div key="step-social" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex flex-col flex-1 items-center pb-4" data-id="element-50">
                <div className="text-center mt-6 mb-8 px-2" data-id="element-51">
                  <h1 className="text-[26px] font-extrabold tracking-tight leading-[1.15] mb-4" data-id="element-52">
                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md" data-id="element-53">
                      Más de 500,000 dueños de perros
                    </span>
                  </h1>
                  <p className="text-[15px] font-medium text-stone-600 leading-relaxed px-2" data-id="element-54">
                    están utilizando la rutina de modales para perros pequeños
                    de <span className="font-bold text-stone-800" data-id="element-55">KALMIA</span>{' '}
                    para reemplazar métodos de entrenamiento obsoletos
                  </p>
                </div>

                {/* Quote card */}
                <div className="w-full bg-blue-50 rounded-[20px] p-6 mb-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-id="element-56">
                  <p className="text-[15px] font-semibold text-stone-700 leading-relaxed italic text-center mb-3" data-id="element-57">
                    "Los perros pequeños no necesitan entrenamiento… necesitan
                    modales."
                  </p>
                  <p className="text-[13px] font-bold text-blue-600 text-center" data-id="element-58">
                    Equipo KALMIA
                  </p>
                </div>

                {/* Credibility section */}
                <div className="flex flex-col items-center mb-10 px-4" data-id="element-59">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4" data-id="element-60">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-61">
                      <path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7L12 16.4 5.7 21l2.3-7L2 9.4h7.6L12 2z" data-id="element-62" />
                    </svg>
                  </div>
                  <p className="text-[13px] font-medium text-stone-500 leading-relaxed text-center" data-id="element-63">
                    Estudios recientes sobre comportamiento canino han sido
                    presentados en{' '}
                    <span className="font-bold text-stone-700" data-id="element-64">Oxford</span>,{' '}
                    <span className="font-bold text-stone-700" data-id="element-65">Harvard</span> y{' '}
                    <span className="font-bold text-stone-700" data-id="element-66">Cambridge</span>
                  </p>
                </div>

                {/* Continue button */}
                <motion.button onClick={() => {
              setStep(5);
            }} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="w-full py-4 rounded-[16px] text-[16px] font-bold tracking-wide bg-orange-500 text-white hover:bg-orange-600 cursor-pointer transition-all duration-300 shadow-[0_4px_20px_rgba(0,0,0,0.06)]" data-id="element-67">
                  Continuar
                </motion.button>

                {/* Back button */}
                <button onClick={() => setStep(3)} className="mt-6 text-sm font-semibold text-stone-400 hover:text-stone-600 transition-colors mx-auto" data-id="element-68">
                  ← Volver
                </button>
              </motion.div>}

            {/* STEP 5 — Progressive Reveal Multi-Section */}
            {step === 5 && <motion.div key="step-progressive" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex flex-col flex-1 pb-4" data-id="element-69">
                <div className="text-center mt-2 mb-8" data-id="element-70">
                  <h1 className="text-[26px] font-extrabold tracking-tight text-stone-900 mb-4 leading-[1.15]" data-id="element-71">
                    ¿QUÉ DE ESTO ESTÁ AFECTANDO MÁS A TU PERRO?
                  </h1>
                  <p className="text-[13px] font-medium text-stone-500 leading-relaxed px-4" data-id="element-72">
                    Selecciona todo lo que te suene familiar — esto nos ayuda a
                    personalizar su rutina
                  </p>
                </div>

                {/* SECTION 1 - Always visible */}
                <div data-id="element-73">
                  <div className="flex flex-col gap-3 mb-6" data-id="element-74">
                    {[{
                  id: 'ladra',
                  title: 'Ladra demasiado (a personas, ruidos o a todo 😩)',
                  emoji: '🗣️'
                }, {
                  id: 'energia',
                  title: 'Demasiada energía (no se calma ni dentro de casa)',
                  emoji: '⚡'
                }, {
                  id: 'agresivo',
                  title: 'Se pone reactivo o agresivo con personas u otros perros',
                  emoji: '😤'
                }, {
                  id: 'correa',
                  title: 'Tira de la correa en los paseos (difícil de controlar)',
                  emoji: '🐕‍🦺'
                }, {
                  id: 'ansiedad',
                  title: 'Se pone ansioso cuando me voy (llora, se altera o rompe cosas)',
                  emoji: '😢'
                }, {
                  id: 'accidentes',
                  title: 'Todavía tiene accidentes dentro de la casa',
                  emoji: '💧'
                }, {
                  id: 'destructivo',
                  title: 'Rompe cosas (muebles, zapatos, etc.)',
                  emoji: '🪑'
                }].map(option => {
                  const isSelected = section1Selections.includes(option.id);
                  return <motion.button key={option.id} onClick={() => handleSection1Toggle(option.id)} whileHover={{
                    scale: 1.015
                  }} whileTap={{
                    scale: 0.98
                  }} initial={false} animate={{
                    borderColor: isSelected ? '#f97316' : 'transparent',
                    backgroundColor: isSelected ? '#fff7ed' : '#ffffff'
                  }} transition={{
                    duration: 0.2,
                    ease: 'easeOut'
                  }} className="relative w-full text-left p-4 rounded-[20px] flex items-center gap-3 border-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.06)]" data-id="element-75">
                          <span className="text-xl shrink-0" data-id="element-76">
                            {option.emoji}
                          </span>
                          <h3 className={`font-bold text-[15px] pr-2 flex-1 transition-colors duration-200 ${isSelected ? 'text-orange-900' : 'text-stone-800'}`} data-id="element-77">
                            {option.title}
                          </h3>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${isSelected ? 'bg-orange-500' : 'bg-stone-100 border-2 border-stone-200'}`} data-id="element-78">
                            {isSelected && <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-79" />}
                          </div>
                        </motion.button>;
                })}
                  </div>

                  {/* Divider */}
                  <div className="flex items-center gap-3 mb-4 px-2" data-id="element-80">
                    <div className="flex-1 h-px bg-stone-200" data-id="element-81" />
                  </div>

                  {/* La verdad… todos */}
                  <motion.button onClick={() => handleSection1Toggle('todas')} whileHover={{
                scale: 1.015
              }} whileTap={{
                scale: 0.98
              }} initial={false} animate={{
                borderColor: section1Selections.includes('todas') ? '#f97316' : '#e7e5e4',
                backgroundColor: section1Selections.includes('todas') ? '#fff7ed' : '#ffffff'
              }} transition={{
                duration: 0.2,
                ease: 'easeOut'
              }} className="relative w-full text-left p-4 rounded-[20px] flex items-center gap-3 border-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.06)] mb-8" data-id="element-82">
                    <span className="text-xl shrink-0" data-id="element-83">😓</span>
                    <h3 className={`font-extrabold text-[15px] pr-2 flex-1 transition-colors duration-200 ${section1Selections.includes('todas') ? 'text-orange-900' : 'text-stone-800'}`} data-id="element-84">
                      La verdad… todos
                    </h3>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${section1Selections.includes('todas') ? 'bg-orange-500' : 'bg-stone-100 border-2 border-stone-200'}`} data-id="element-85">
                      {section1Selections.includes('todas') && <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-86" />}
                    </div>
                  </motion.button>
                </div>

                {/* SECTION 2 - Revealed after section 1 */}
                <AnimatePresence data-id="element-87">
                  {section1Selections.length > 0 && <motion.div ref={section2Ref} variants={sectionVariants} initial="hidden" animate="visible" transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} data-id="element-88">
                      <p className="text-[11px] font-bold text-stone-400 tracking-[0.15em] uppercase mb-3 ml-1" data-id="element-89">
                        ¿Qué motiva más a tu perro?
                      </p>
                      <div className="flex flex-col gap-3 mb-8" data-id="element-90">
                        {[{
                    id: 'comida',
                    title: 'Comida / premios'
                  }, {
                    id: 'juguetes',
                    title: 'Juguetes'
                  }, {
                    id: 'voz',
                    title: 'Mi voz'
                  }, {
                    id: 'noseguro',
                    title: 'No estoy seguro'
                  }].map(option => {
                    const isSelected = section2Selection === option.id;
                    return <motion.button key={option.id} onClick={() => handleSection2Select(option.id)} whileHover={{
                      scale: 1.015
                    }} whileTap={{
                      scale: 0.98
                    }} initial={false} animate={{
                      borderColor: isSelected ? '#f97316' : 'transparent',
                      backgroundColor: isSelected ? '#fff7ed' : '#ffffff'
                    }} transition={{
                      duration: 0.2,
                      ease: 'easeOut'
                    }} className="relative w-full text-left p-4 rounded-[20px] flex items-center justify-between border-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.06)]" data-id="element-91">
                              <h3 className={`font-bold text-[15px] pr-4 transition-colors duration-200 ${isSelected ? 'text-orange-900' : 'text-stone-800'}`} data-id="element-92">
                                {option.title}
                              </h3>
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${isSelected ? 'bg-orange-500' : 'bg-stone-100 border-2 border-stone-200'}`} data-id="element-93">
                                {isSelected && <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-94" />}
                              </div>
                            </motion.button>;
                  })}
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* SECTION 3 - Revealed after section 2 */}
                <AnimatePresence data-id="element-95">
                  {section2Selection && <motion.div ref={section3Ref} variants={sectionVariants} initial="hidden" animate="visible" transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} data-id="element-96">
                      <p className="text-[11px] font-bold text-stone-400 tracking-[0.15em] uppercase mb-3 ml-1" data-id="element-97">
                        ¿Qué tanta experiencia tienes?
                      </p>
                      <div className="flex flex-col gap-3 mb-8" data-id="element-98">
                        {[{
                    id: 'ninguna',
                    title: 'Ninguna'
                  }, {
                    id: 'poco',
                    title: 'Un poco'
                  }, {
                    id: 'bastante',
                    title: 'Bastante'
                  }].map(option => {
                    const isSelected = section3Selection === option.id;
                    return <motion.button key={option.id} onClick={() => handleSection3Select(option.id)} whileHover={{
                      scale: 1.015
                    }} whileTap={{
                      scale: 0.98
                    }} initial={false} animate={{
                      borderColor: isSelected ? '#f97316' : 'transparent',
                      backgroundColor: isSelected ? '#fff7ed' : '#ffffff'
                    }} transition={{
                      duration: 0.2,
                      ease: 'easeOut'
                    }} className="relative w-full text-left p-4 rounded-[20px] flex items-center justify-between border-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.06)]" data-id="element-99">
                              <h3 className={`font-bold text-[15px] pr-4 transition-colors duration-200 ${isSelected ? 'text-orange-900' : 'text-stone-800'}`} data-id="element-100">
                                {option.title}
                              </h3>
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${isSelected ? 'bg-orange-500' : 'bg-stone-100 border-2 border-stone-200'}`} data-id="element-101">
                                {isSelected && <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-102" />}
                              </div>
                            </motion.button>;
                  })}
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* SECTION 4 - Revealed after section 3 */}
                <AnimatePresence data-id="element-103">
                  {section3Selection && <motion.div ref={section4Ref} variants={sectionVariants} initial="hidden" animate="visible" transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} data-id="element-104">
                      <p className="text-[11px] font-bold text-stone-400 tracking-[0.15em] uppercase mb-3 ml-1" data-id="element-105">
                        ¿Qué te ha dificultado entrenarlo?
                      </p>
                      <div className="flex flex-col gap-3 mb-6" data-id="element-106">
                        {[{
                    id: 'tiempo',
                    title: 'No tengo tiempo'
                  }, {
                    id: 'resultados',
                    title: 'No veo resultados'
                  }, {
                    id: 'plan',
                    title: 'No tengo un plan'
                  }, {
                    id: 'dificil',
                    title: 'Es difícil entrenarlo'
                  }, {
                    id: 'guia',
                    title: 'No tuve buena guía'
                  }].map(option => {
                    const isSelected = section4Selections.includes(option.id);
                    return <motion.button key={option.id} onClick={() => handleSection4Toggle(option.id)} whileHover={{
                      scale: 1.015
                    }} whileTap={{
                      scale: 0.98
                    }} initial={false} animate={{
                      borderColor: isSelected ? '#f97316' : 'transparent',
                      backgroundColor: isSelected ? '#fff7ed' : '#ffffff'
                    }} transition={{
                      duration: 0.2,
                      ease: 'easeOut'
                    }} className="relative w-full text-left p-4 rounded-[20px] flex items-center justify-between border-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.06)]" data-id="element-107">
                              <h3 className={`font-bold text-[15px] pr-4 transition-colors duration-200 ${isSelected ? 'text-orange-900' : 'text-stone-800'}`} data-id="element-108">
                                {option.title}
                              </h3>
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${isSelected ? 'bg-orange-500' : 'bg-stone-100 border-2 border-stone-200'}`} data-id="element-109">
                                {isSelected && <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-110" />}
                              </div>
                            </motion.button>;
                  })}
                      </div>

                      {/* Divider */}
                      <div className="flex items-center gap-3 mb-4 px-2" data-id="element-111">
                        <div className="flex-1 h-px bg-stone-200" data-id="element-112" />
                      </div>

                      {/* Todas las anteriores */}
                      <motion.button onClick={() => handleSection4Toggle('todas')} whileHover={{
                  scale: 1.015
                }} whileTap={{
                  scale: 0.98
                }} initial={false} animate={{
                  borderColor: section4Selections.includes('todas') ? '#f97316' : '#e7e5e4',
                  backgroundColor: section4Selections.includes('todas') ? '#fff7ed' : '#ffffff'
                }} transition={{
                  duration: 0.2,
                  ease: 'easeOut'
                }} className="relative w-full text-left p-4 rounded-[20px] flex items-center justify-between border-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.06)] mb-8" data-id="element-113">
                        <h3 className={`font-extrabold text-[15px] pr-4 transition-colors duration-200 ${section4Selections.includes('todas') ? 'text-orange-900' : 'text-stone-800'}`} data-id="element-114">
                          Todas las anteriores
                        </h3>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${section4Selections.includes('todas') ? 'bg-orange-500' : 'bg-stone-100 border-2 border-stone-200'}`} data-id="element-115">
                          {section4Selections.includes('todas') && <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-116" />}
                        </div>
                      </motion.button>
                    </motion.div>}
                </AnimatePresence>

                {/* CTA Button */}
                <motion.button onClick={() => setStep(6)} disabled={section1Selections.length === 0 || !section2Selection} whileHover={section1Selections.length > 0 && section2Selection ? {
              scale: 1.02
            } : {}} whileTap={section1Selections.length > 0 && section2Selection ? {
              scale: 0.98
            } : {}} className={`w-full py-4 rounded-[16px] text-[16px] font-bold tracking-wide transition-all duration-300 shadow-[0_4px_20px_rgba(0,0,0,0.06)] ${section1Selections.length > 0 && section2Selection ? 'bg-orange-500 text-white hover:bg-orange-600 cursor-pointer' : 'bg-stone-200 text-stone-400 cursor-not-allowed'}`} data-id="element-117">
                  Crear la rutina de mi perro
                </motion.button>

                {/* Back button */}
                <button onClick={() => {
              setStep(4);
              setSection1Selections([]);
              setSection2Selection(null);
              setSection3Selection(null);
              setSection4Selections([]);
            }} className="mt-6 text-sm font-semibold text-stone-400 hover:text-stone-600 transition-colors mx-auto" data-id="element-118">
                  ← Volver
                </button>
              </motion.div>}

            {/* STEP 6 — Pet Name */}
            {step === 6 && <motion.div key="step-petname" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex flex-col flex-1 pb-4" data-id="element-119">
                <div className="text-center mt-2 mb-10" data-id="element-120">
                  <h1 className="text-[26px] font-extrabold tracking-tight text-stone-900 mb-4 leading-[1.15]" data-id="element-121">
                    TU PERRO MERECE UNA RUTINA HECHA PARA ÉL 🐶
                  </h1>
                  <p className="text-xs font-bold text-stone-400 tracking-[0.15em] uppercase leading-relaxed px-4" data-id="element-122">
                    Antes de continuar, dinos su nombre para personalizar todo
                  </p>
                </div>

                {/* Name input */}
                <div className="mb-4" data-id="element-123">
                  <input type="text" value={petName} onChange={e => setPetName(e.target.value)} placeholder="Ej: Max, Luna, Toby..." className="w-full bg-white border-2 border-transparent rounded-[16px] px-5 py-4 text-[16px] font-medium text-stone-800 shadow-[0_4px_20px_rgba(0,0,0,0.03)] focus:outline-none focus:border-orange-500 focus:ring-0 transition-colors duration-200 placeholder:text-stone-400" autoFocus data-id="element-124" />
                </div>

                {/* Microcopy */}
                <p className="text-[13px] font-medium text-stone-400 text-center mb-8 px-4" data-id="element-125">
                  Usaremos su nombre para crear una rutina 100% personalizada
                </p>

                {/* Dynamic preview */}
                <AnimatePresence data-id="element-126">
                  {petName.trim().length > 0 && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.3,
                ease: 'easeOut'
              }} className="w-full bg-orange-50 rounded-[20px] p-5 mb-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-id="element-127">
                      <p className="text-[15px] font-semibold text-orange-800 leading-relaxed text-center" data-id="element-128">
                        Estamos preparando una rutina perfecta para{' '}
                        <span className="font-extrabold" data-id="element-129">{petName.trim()}</span>
                        ...
                      </p>
                    </motion.div>}
                </AnimatePresence>

                {/* CTA Button */}
                <motion.button onClick={() => setStep(7)} disabled={petName.trim().length === 0} whileHover={petName.trim().length > 0 ? {
              scale: 1.02
            } : {}} whileTap={petName.trim().length > 0 ? {
              scale: 0.98
            } : {}} className={`w-full py-4 rounded-[16px] text-[16px] font-bold tracking-wide transition-all duration-300 shadow-[0_4px_20px_rgba(0,0,0,0.06)] ${petName.trim().length > 0 ? 'bg-orange-500 text-white hover:bg-orange-600 cursor-pointer' : 'bg-stone-200 text-stone-400 cursor-not-allowed'}`} data-id="element-130">
                  {petName.trim().length > 0 ? `Crear rutina para ${petName.trim()}` : 'Crear rutina para tu perro'}
                </motion.button>

                {/* Back button */}
                <button onClick={() => {
              setStep(5);
              setPetName('');
            }} className="mt-6 text-sm font-semibold text-stone-400 hover:text-stone-600 transition-colors mx-auto" data-id="element-131">
                  ← Volver
                </button>
              </motion.div>}

            {/* STEP 7 — Potty Training */}
            {step === 7 && <motion.div key="step-potty" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex flex-col flex-1 pb-4" data-id="element-132">
                <div className="text-center mt-2 mb-8" data-id="element-133">
                  <h1 className="text-[26px] font-extrabold tracking-tight text-stone-900 mb-4 leading-[1.15]" data-id="element-134">
                    ¿Dónde está teniendo más problemas tu cachorro con el
                    entrenamiento para ir al baño?
                  </h1>
                  <p className="text-xs font-bold text-stone-400 tracking-[0.15em] uppercase leading-relaxed px-4" data-id="element-135">
                    Sé honesto — esto nos ayuda a crear una rutina que realmente
                    funcione
                  </p>
                </div>

                <div className="flex flex-col gap-3 mb-6" data-id="element-136">
                  {[{
                id: 'schedule',
                title: 'No hay una rutina fija para ir al baño (los accidentes ocurren al azar)'
              }, {
                id: 'night',
                title: 'Accidentes nocturnos (despertar con desastres 😔)'
              }, {
                id: 'alone',
                title: 'Accidentes cuando no estoy en casa (no puedo controlarlo)'
              }].map(option => {
                const isSelected = pottySelections.includes(option.id);
                return <motion.button key={option.id} onClick={() => handlePottyToggle(option.id)} whileHover={{
                  scale: 1.015
                }} whileTap={{
                  scale: 0.98
                }} initial={false} animate={{
                  borderColor: isSelected ? '#f97316' : 'transparent',
                  backgroundColor: isSelected ? '#fff7ed' : '#ffffff'
                }} transition={{
                  duration: 0.2,
                  ease: 'easeOut'
                }} className="relative w-full text-left p-4 rounded-[20px] flex items-center justify-between border-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.06)]" data-id="element-137">
                        <h3 className={`font-bold text-[15px] pr-4 transition-colors duration-200 ${isSelected ? 'text-orange-900' : 'text-stone-800'}`} data-id="element-138">
                          {option.title}
                        </h3>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${isSelected ? 'bg-orange-500' : 'bg-stone-100 border-2 border-stone-200'}`} data-id="element-139">
                          {isSelected && <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-140" />}
                        </div>
                      </motion.button>;
              })}
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-4 px-2" data-id="element-141">
                  <div className="flex-1 h-px bg-stone-200" data-id="element-142" />
                </div>

                {/* All of the above */}
                <motion.button onClick={() => handlePottyToggle('todas')} whileHover={{
              scale: 1.015
            }} whileTap={{
              scale: 0.98
            }} initial={false} animate={{
              borderColor: pottySelections.includes('todas') ? '#f97316' : '#e7e5e4',
              backgroundColor: pottySelections.includes('todas') ? '#fff7ed' : '#ffffff'
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="relative w-full text-left p-4 rounded-[20px] flex items-center justify-between border-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.06)] mb-8" data-id="element-143">
                  <h3 className={`font-extrabold text-[15px] pr-4 transition-colors duration-200 ${pottySelections.includes('todas') ? 'text-orange-900' : 'text-stone-800'}`} data-id="element-144">
                    Todas las anteriores 😩
                  </h3>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${pottySelections.includes('todas') ? 'bg-orange-500' : 'bg-stone-100 border-2 border-stone-200'}`} data-id="element-145">
                    {pottySelections.includes('todas') && <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-146" />}
                  </div>
                </motion.button>

                {/* CTA Button */}
                <motion.button onClick={() => setStep(8)} disabled={pottySelections.length === 0} whileHover={pottySelections.length > 0 ? {
              scale: 1.02
            } : {}} whileTap={pottySelections.length > 0 ? {
              scale: 0.98
            } : {}} className={`w-full py-4 rounded-[16px] text-[16px] font-bold tracking-wide transition-all duration-300 shadow-[0_4px_20px_rgba(0,0,0,0.06)] ${pottySelections.length > 0 ? 'bg-orange-500 text-white hover:bg-orange-600 cursor-pointer' : 'bg-stone-200 text-stone-400 cursor-not-allowed'}`} data-id="element-147">
                  Ayúdame a solucionarlo
                </motion.button>

                {/* Back button */}
                <button onClick={() => {
              setStep(6);
              setPottySelections([]);
            }} className="mt-6 text-sm font-semibold text-stone-400 hover:text-stone-600 transition-colors mx-auto" data-id="element-148">
                  ← Volver
                </button>
              </motion.div>}

            {/* STEP 8 — Personalized 28-Day Plan Calendar */}
            {step === 8 && <motion.div key="step-vision" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex flex-col flex-1 pb-4" data-id="element-149">
                <div className="text-center mt-2 mb-6 px-2" data-id="element-150">
                  <h1 className="text-[26px] font-extrabold tracking-tight text-stone-900 mb-4 leading-[1.15]" data-id="element-151">
                    EL PLAN PERSONALIZADO DE{' '}
                    <span className="text-orange-600" data-id="element-152">
                      {(petName.trim() || 'TU PERRO').toUpperCase()}
                    </span>{' '}
                    ESTÁ CASI LISTO 🐶
                  </h1>
                  <p className="text-[13px] font-medium text-stone-500 leading-relaxed px-2" data-id="element-153">
                    Estamos creando una rutina paso a paso basada en sus
                    respuestas
                  </p>
                </div>

                {/* Main motivational text */}
                <div className="w-full bg-white rounded-[20px] p-5 mb-5 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-id="element-154">
                  <p className="text-[14px] font-medium text-stone-600 leading-relaxed text-center" data-id="element-155">
                    Es momento de dejar de improvisar y empezar a guiar a{' '}
                    <span className="font-bold text-orange-600" data-id="element-156">
                      {petName.trim() || 'tu perro'}
                    </span>{' '}
                    con una rutina clara y efectiva.
                  </p>
                </div>

                {/* 28-Day Calendar Grid */}
                <div className="w-full bg-white rounded-[20px] p-5 mb-5 shadow-[0_4px_20px_rgba(0,0,0,0.03)]" data-id="element-157">
                  <div className="flex items-center justify-between mb-4" data-id="element-158">
                    <p className="text-[13px] font-bold text-stone-700 tracking-wide" data-id="element-159">
                      Plan de 28 días
                    </p>
                    <p className="text-[12px] font-semibold text-orange-500" data-id="element-160">
                      4 semanas
                    </p>
                  </div>

                  {/* Week labels */}
                  <div className="grid grid-cols-7 gap-1.5 mb-2" data-id="element-161">
                    {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(day => <div key={day} className="text-[10px] font-bold text-stone-400 text-center" data-id="element-162">
                        {day}
                      </div>)}
                  </div>

                  {/* Calendar days */}
                  <div className="grid grid-cols-7 gap-1.5" data-id="element-163">
                    {Array.from({
                  length: 28
                }, (_, i) => {
                  const dayNum = i + 1;
                  const isCompleted = dayNum <= 3;
                  const isToday = dayNum === 4;
                  const isWeekStart = dayNum === 1 || dayNum === 8 || dayNum === 15 || dayNum === 22;
                  return <motion.div key={dayNum} initial={{
                    opacity: 0,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} transition={{
                    delay: i * 0.02,
                    duration: 0.3,
                    ease: 'easeOut'
                  }} className={`aspect-square rounded-xl flex items-center justify-center text-[12px] font-bold relative ${isCompleted ? 'bg-orange-500 text-white shadow-sm' : isToday ? 'bg-orange-100 text-orange-700 ring-2 ring-orange-400' : 'bg-stone-50 text-stone-400'}`} data-id="element-164">
                            {isCompleted ? <CheckIcon className="w-3.5 h-3.5" strokeWidth={3} data-id="element-165" /> : dayNum}
                            {isToday && <div className="absolute -bottom-0.5 w-1 h-1 bg-orange-500 rounded-full" data-id="element-166" />}
                          </motion.div>;
                })}
                  </div>

                  {/* Week milestones */}
                  <div className="mt-4 flex flex-col gap-2" data-id="element-167">
                    {[{
                  week: 'Semana 1',
                  label: 'Fundamentos y hábitos básicos',
                  icon: '🌱'
                }, {
                  week: 'Semana 2',
                  label: 'Refuerzo y consistencia',
                  icon: '💪'
                }, {
                  week: 'Semana 3',
                  label: 'Avance y nuevos retos',
                  icon: '🎯'
                }, {
                  week: 'Semana 4',
                  label: 'Consolidación y resultados',
                  icon: '🏆'
                }].map(milestone => <div key={milestone.week} className="flex items-center gap-2.5" data-id="element-168">
                        <span className="text-sm" data-id="element-169">{milestone.icon}</span>
                        <div className="flex-1" data-id="element-170">
                          <span className="text-[11px] font-bold text-stone-500" data-id="element-171">
                            {milestone.week}
                          </span>
                          <span className="text-[11px] text-stone-400 ml-1.5" data-id="element-172">
                            — {milestone.label}
                          </span>
                        </div>
                      </div>)}
                  </div>
                </div>

                {/* Benefit section */}

                {/* Micro personalization based on previous answers */}

                {/* Pain point block */}

                {/* Micro-tension */}
                <p className="text-[13px] font-bold text-stone-700 leading-relaxed text-center mb-6 px-4" data-id="element-173">
                  Por eso esta rutina no es genérica…
                  <br data-id="element-174" />
                  <span className="text-orange-600" data-id="element-175">
                    está diseñada específicamente para{' '}
                    {petName.trim() || 'tu perro'}.
                  </span>
                </p>

                {/* Continue button */}
                <motion.button onClick={() => {
              setStep(9);
            }} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="w-full py-4 rounded-[16px] text-[16px] font-bold tracking-wide bg-orange-500 text-white hover:bg-orange-600 cursor-pointer transition-all duration-300 shadow-[0_4px_20px_rgba(0,0,0,0.06)]" data-id="element-176">
                  Ver la rutina personalizada de {petName.trim() || 'tu perro'}
                </motion.button>

                {/* Time reassurance */}
                <p className="mt-3 text-[12px] font-medium text-stone-400 text-center" data-id="element-177">
                  Toma menos de 1 minuto ver el plan completo
                </p>

                {/* Back button */}
                <button onClick={() => {
              setStep(7);
            }} className="mt-6 text-sm font-semibold text-stone-400 hover:text-stone-600 transition-colors mx-auto" data-id="element-178">
                  ← Volver
                </button>
              </motion.div>}

            {/* STEP 9 — Situation Identification */}
            {step === 9 && <motion.div key="step-situations" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex flex-col flex-1 pb-4" data-id="element-179">
                <div className="text-center mt-2 mb-10" data-id="element-180">
                  <h1 className="text-[26px] font-extrabold tracking-tight text-stone-900 mb-4 leading-[1.15]" data-id="element-181">
                    ¿TE IDENTIFICAS CON ALGUNA DE ESTAS SITUACIONES?
                  </h1>
                  <p className="text-xs font-bold text-stone-400 tracking-[0.15em] uppercase leading-relaxed px-4" data-id="element-182">
                    Selecciona las que más te representen
                  </p>
                </div>

                <div className="flex flex-col gap-4" data-id="element-183">
                  {situationOptions.map(option => {
                const isSelected = selectedSituations.includes(option.id);
                return <motion.button key={option.id} onClick={() => handleSituationToggle(option.id)} whileHover={{
                  scale: 1.015
                }} whileTap={{
                  scale: 0.98
                }} initial={false} animate={{
                  borderColor: isSelected ? '#f97316' : 'transparent',
                  backgroundColor: isSelected ? '#fff7ed' : '#ffffff'
                }} transition={{
                  duration: 0.2,
                  ease: 'easeOut'
                }} className="relative w-full text-left p-5 rounded-[20px] flex items-center justify-between border-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.06)]" data-id="element-184">
                        <h3 className={`font-bold text-[16px] pr-4 transition-colors duration-200 ${isSelected ? 'text-orange-900' : 'text-stone-800'}`} data-id="element-185">
                          {option.title}
                        </h3>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${isSelected ? 'bg-orange-500' : 'bg-stone-100 border-2 border-stone-200'}`} data-id="element-186">
                          {isSelected && <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-187" />}
                        </div>
                        {isSelected && <motion.div layoutId="situationDot" className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-orange-500 rounded-full shadow-sm" data-id="element-188" />}
                      </motion.button>;
              })}
                </div>

                {/* Continue button */}
                <motion.button onClick={() => {
              setStep(10);
            }} disabled={selectedSituations.length === 0} whileHover={selectedSituations.length > 0 ? {
              scale: 1.02
            } : {}} whileTap={selectedSituations.length > 0 ? {
              scale: 0.98
            } : {}} className={`mt-8 w-full py-4 rounded-[16px] text-[16px] font-bold tracking-wide transition-all duration-300 shadow-[0_4px_20px_rgba(0,0,0,0.06)] ${selectedSituations.length > 0 ? 'bg-orange-500 text-white hover:bg-orange-600 cursor-pointer' : 'bg-stone-200 text-stone-400 cursor-not-allowed'}`} data-id="element-189">
                  Continuar
                </motion.button>

                {/* Back button */}
                <button onClick={() => {
              setStep(8);
              setSelectedSituations([]);
            }} className="mt-6 text-sm font-semibold text-stone-400 hover:text-stone-600 transition-colors mx-auto" data-id="element-190">
                  ← Volver
                </button>
              </motion.div>}

            {/* STEP 10 — Validation Transition */}
            {step === 10 && <motion.div key="step-validation" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex flex-col flex-1 items-center pb-4" data-id="element-191">
                <div className="text-center mt-6 mb-6 px-2" data-id="element-192">
                  <h1 className="text-[26px] font-extrabold tracking-tight text-stone-900 mb-3 leading-[1.15]" data-id="element-193">
                    ¿POR QUÉ LA MAYORÍA DE LAS PERSONAS NO LOGRA EDUCAR A SU
                    PERRO?
                  </h1>
                  <p className="text-[15px] font-semibold text-orange-600 leading-relaxed px-2" data-id="element-194">
                    La razón #1: intentar hacer demasiado, demasiado rápido
                  </p>
                </div>

                {/* Identification block */}

                {/* Personal block */}

                {/* Expert card */}
                <motion.div initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.6,
              duration: 0.4,
              ease: 'easeOut'
            }} className="w-full bg-white rounded-[20px] p-5 mb-6 shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-stone-100" data-id="element-195">
                  <div className="flex items-start gap-4" data-id="element-196">
                    <div className="w-12 h-12 rounded-full overflow-hidden shrink-0" data-id="element-197">
                      <img src="https://cdn.magicpatterns.com/uploads/d45eeYkJaZfz96opoLt2Y9/mesa-de-trabajo-11_802.webp" alt="Dr. Lisa V." className="w-full h-full object-cover" data-id="element-198" />
                    </div>
                    <div className="flex-1" data-id="element-199">
                      <p className="text-[14px] font-bold text-stone-800 mb-0.5" data-id="element-200">
                        Dr. Lisa V.
                      </p>
                      <p className="text-[12px] font-semibold text-orange-600 mb-3" data-id="element-201">
                        Especialista en comportamiento canino
                      </p>
                      <p className="text-[14px] font-medium text-stone-600 leading-relaxed italic" data-id="element-202">
                        "Por eso nuestra rutina está diseñada paso a paso, para
                        que tu perro aprenda sin frustración… y tú empieces a
                        ver cambios reales desde los primeros días."
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Continue button */}
                <motion.button onClick={() => {
              setStep(11);
            }} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="w-full py-4 rounded-[16px] text-[16px] font-bold tracking-wide bg-orange-500 text-white hover:bg-orange-600 cursor-pointer transition-all duration-300 shadow-[0_4px_20px_rgba(0,0,0,0.06)]" data-id="element-203">
                  Ver cómo funciona
                </motion.button>

                {/* Back button */}
                <button onClick={() => setStep(9)} className="mt-6 text-sm font-semibold text-stone-400 hover:text-stone-600 transition-colors mx-auto" data-id="element-204">
                  ← Volver
                </button>
              </motion.div>}

            {/* STEP 11 — Diagnostic Results */}
            {step === 11 && <motion.div key="step-score" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex flex-col flex-1 pb-6 pt-2" data-id="element-205">
                <AnimatePresence mode="wait" data-id="element-206">
                  {step11Loading ? <ProgressLoader key="loader" petName={petName.trim() || 'tu perro'} onComplete={() => setStep11Loading(false)} data-id="element-207" /> : <motion.div key="results" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 0.4
              }} className="flex flex-col flex-1" data-id="element-208">
                      {(() => {
                  const name = petName.trim() || 'Tu perro';
                  const diagnosticoTipo = section1Selections.includes('energia') ? 'energia' : section1Selections.includes('correa') || section1Selections.includes('agresivo') ? 'obediencia' : section1Selections.includes('ansiedad') ? 'ansiedad' : 'default';
                  const tituloDiagnostico = {
                    energia: `${name} tiene más energía de la que sabe manejar… y eso está afectando su comportamiento.`,
                    obediencia: `${name} entiende más de lo que parece… pero no está respondiendo en los momentos clave.`,
                    ansiedad: `${name} está reaccionando más por impulso que por calma… y eso se está volviendo un patrón.`,
                    default: `Hay una desconexión silenciosa en la forma en que ${name} responde a su entorno…`
                  };
                  return <>
                            {/* Diagnostic Header Card */}
                            <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.5,
                      ease: 'easeOut'
                    }} className="w-full bg-white rounded-[16px] p-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-[#f1f1f1]" data-id="element-209">
                              {/* Score */}
                              <p className="text-[11px] font-bold text-stone-400 tracking-[0.15em] uppercase mb-2 text-center" data-id="element-210">
                                Diagnóstico de comportamiento
                              </p>
                              <div className="flex items-baseline justify-center gap-1 mb-4" data-id="element-211">
                                <span className="text-[56px] font-extrabold leading-none tracking-tight" style={{
                          color: mannersScore <= 4 ? '#ef4444' : mannersScore <= 7 ? '#eab308' : '#22c55e'
                        }} data-id="element-212">
                                  {mannersScore}
                                </span>
                                <span className="text-[22px] font-bold text-stone-300" data-id="element-213">
                                  / 10
                                </span>
                              </div>

                              {/* Score bar */}
                              <div className="w-full h-4 bg-stone-100 rounded-full overflow-hidden mb-6 relative" data-id="element-214">
                                <motion.div initial={{
                          width: 0
                        }} animate={{
                          width: `${scorePercent}%`
                        }} transition={{
                          delay: 0.3,
                          duration: 1,
                          ease: 'easeOut'
                        }} className="h-full rounded-full" style={{
                          background: mannersScore <= 4 ? 'linear-gradient(90deg, #ef4444, #f87171)' : mannersScore <= 7 ? 'linear-gradient(90deg, #eab308, #facc15)' : 'linear-gradient(90deg, #22c55e, #4ade80)'
                        }} data-id="element-215" />
                              </div>

                              {/* Dynamic diagnostic title */}
                              <motion.p initial={{
                        opacity: 0,
                        y: 8
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} transition={{
                        delay: 0.5,
                        duration: 0.4
                      }} className="text-[16px] font-bold text-stone-800 text-center leading-relaxed mb-3" data-id="element-216">
                                {tituloDiagnostico[diagnosticoTipo]}
                              </motion.p>

                              {/* Fixed subtext */}
                              <motion.p initial={{
                        opacity: 0
                      }} animate={{
                        opacity: 1
                      }} transition={{
                        delay: 0.7,
                        duration: 0.4
                      }} className="text-[13px] font-medium text-stone-500 text-center leading-relaxed" data-id="element-217">
                                No es falta de inteligencia… es falta de
                                estructura en los momentos clave.
                              </motion.p>
                            </motion.div>

                            {/* Behavior List */}

                            {/* Emotional Red Block */}

                            {/* Positive Green Block */}

                            {/* Progress Chart Card */}
                            <motion.div initial={{
                      opacity: 0,
                      y: 14
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: 1.6,
                      duration: 0.5
                    }} className="mt-4 w-full bg-white rounded-[16px] p-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-[#f1f1f1]" data-id="element-218">
                              <p className="text-[12px] font-bold text-stone-500 tracking-wide uppercase mb-4 text-center" data-id="element-219">
                                Proyección con la rutina
                              </p>
                              <div className="flex justify-between items-end h-28 gap-2 mb-2" data-id="element-220">
                                {[0, 1, 2, 3, 4, 5, 6].map(stepIdx => {
                          const isLast = stepIdx === 6;
                          const isFirst = stepIdx === 0;
                          const startHeight = Math.max(20, mannersScore / 10 * 100);
                          const endHeight = 95;
                          const height = startHeight + (endHeight - startHeight) * (stepIdx / 6);
                          const isLow = mannersScore <= 4;
                          const isMed = mannersScore > 4 && mannersScore <= 7;
                          let colorClass = '';
                          if (isLast) {
                            colorClass = 'bg-gradient-to-t from-green-400 to-green-500';
                          } else if (isFirst) {
                            colorClass = isLow ? 'bg-gradient-to-t from-red-400 to-red-500' : isMed ? 'bg-gradient-to-t from-yellow-400 to-yellow-500' : 'bg-gradient-to-t from-green-400 to-green-500';
                          } else {
                            if (stepIdx > 3) {
                              colorClass = 'bg-gradient-to-t from-green-300 to-green-400';
                            } else if (stepIdx > 1) {
                              colorClass = isLow ? 'bg-gradient-to-t from-yellow-400 to-yellow-500' : 'bg-gradient-to-t from-green-300 to-green-400';
                            } else {
                              colorClass = isLow ? 'bg-gradient-to-t from-red-300 to-red-400' : isMed ? 'bg-gradient-to-t from-yellow-300 to-yellow-400' : 'bg-gradient-to-t from-green-300 to-green-400';
                            }
                          }
                          return <div key={stepIdx} className="flex-1 flex flex-col items-center justify-end h-full relative" data-id="element-221">
                                      {isLast && <motion.div initial={{
                              opacity: 0,
                              y: 10
                            }} animate={{
                              opacity: 1,
                              y: 0
                            }} transition={{
                              delay: 2.2,
                              duration: 0.4
                            }} className="absolute -top-7 bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-md whitespace-nowrap shadow-sm" data-id="element-222">
                                          Meta
                                        </motion.div>}
                                      <motion.div initial={{
                              height: 0
                            }} animate={{
                              height: `${height}%`
                            }} transition={{
                              delay: 1.6 + stepIdx * 0.1,
                              duration: 0.6,
                              ease: [0.22, 1, 0.36, 1]
                            }} className={`w-full rounded-t-md opacity-90 ${colorClass}`} data-id="element-223" />
                                    </div>;
                        })}
                              </div>
                              <div className="flex justify-between items-center mt-2" data-id="element-224">
                                <span className="text-[11px] font-bold text-stone-400" data-id="element-225">
                                  Hoy
                                </span>
                                <motion.span initial={{
                          opacity: 0
                        }} animate={{
                          opacity: 1
                        }} transition={{
                          delay: 2.2
                        }} className="text-[11px] font-extrabold text-green-600 bg-green-50 px-2 py-0.5 rounded-md" data-id="element-226">
                                  {new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                                </motion.span>
                              </div>
                            </motion.div>

                            {/* Micro text */}
                            <motion.p initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} transition={{
                      delay: 2,
                      duration: 0.4
                    }} className="text-[13px] font-medium text-stone-500 text-center mt-5 leading-relaxed px-2" data-id="element-227">
                              Por eso creamos una rutina específica para{' '}
                              <span className="font-bold text-orange-600" data-id="element-228">
                                {name}
                              </span>{' '}
                              basada en tus respuestas.
                            </motion.p>
                          </>;
                })()}

                      {/* CTA */}
                      <motion.div initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 2.2,
                  duration: 0.5,
                  ease: 'easeOut'
                }} className="mt-6 flex flex-col items-center" data-id="element-229">
                        <motion.button onClick={() => setStep(12)} whileHover={{
                    scale: 1.02
                  }} whileTap={{
                    scale: 0.98
                  }} className="w-full py-5 rounded-[16px] tracking-wide bg-orange-500 text-white hover:bg-orange-600 shadow-[0_4px_20px_rgba(249,115,22,0.25)] transition-all text-[16px] font-[700] pt-[14px] pb-[14px]" data-id="element-230">
                          Crear la rutina de {petName.trim() || 'tu perro'}{' '}
                          ahora
                        </motion.button>
                        <p className="mt-4 text-[13px] font-medium text-stone-400" data-id="element-231">
                          Empieza hoy y verás cambios más rápido
                        </p>
                      </motion.div>

                      {/* Back button */}
                      <button onClick={() => {
                  setStep(10);
                  setStep11Loading(true);
                }} className="mt-6 text-sm font-semibold text-stone-400 hover:text-stone-600 transition-colors mx-auto" data-id="element-232">
                        ← Volver
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>}
            {/* STEP 12 — Routine Loading + Email Capture */}
            {step === 12 && <motion.div key="step-routine-loader" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex flex-col flex-1" data-id="element-233">
                <RoutineLoader petName={petName.trim()} data-id="element-234" />
              </motion.div>}
          </AnimatePresence>
        </main>

        {/* Footer */}
        <footer className="p-8 mt-auto text-center" data-id="element-235">
          <p className="text-[12px] font-medium text-stone-400 leading-relaxed max-w-[260px] mx-auto" data-id="element-236">
            Al continuar, aceptas los términos de uso y políticas de privacidad.
          </p>
        </footer>
      </div>
    </div>;
}